import React, { useMemo, useState } from "react"
import dayjs from "dayjs"
import type {
  DunningCaseContractDetail,
  DunningCaseFacts,
  DunningCaseFactsContract,
  DunningCaseItem,
  DunningPaymentPromise,
  DunningPaymentPromiseOutcome,
  DunningPaymentPromiseStatus,
  DunningPaymentScheduleEntry,
} from "../types"
import { useDunningPaymentPromises } from "../hooks/useDunningPaymentPromises"
import {
  formatDateTime,
  formatJapaneseDate,
  formatYen,
  NEUTRAL_CHIP_CLASS,
  Panel,
  PROMISE_OUTCOME_CONFIG,
  type PromiseChipConfig,
} from "./shared"

/**
 * 詳細画面: 契約単位の情報。
 * 契約タブの選択状態は親（DunningCaseDetail）が持ち、右カラムの統合契約情報パネル
 * （契約情報 + 支払いスケジュール）が連動する。
 * 支払約束（PromisePanel）はケース単位で表示する。
 * 白紙契約（active: false）はタブに出さない。
 * ケースに紐付いていない契約（linked: false）も参考に出し、進行中なら契約情報の見出しに「延滞なし」ラベルを出す。
 * 完済の契約（paidOff）はタブの「XX回払い」を「完済」に替え、契約情報をグレーアウトする。
 */

export type ScheduleRowState = "paid" | "part" | "awaiting" | "overdue" | "future"

const nextDayStart = (date: string) => dayjs(date).startOf("day").add(1, "day")

const laterOf = (left: dayjs.Dayjs, right: dayjs.Dayjs) => (left.isAfter(right) ? left : right)

/**
 * 分割が遅延中になる時刻。サーバーの延滞確定（ConfirmedDelinquentInstallmentsScope と
 * ContractsWithDelinquentInstallmentsQuery の scheduled_on < 今日）と同じ定義にそろえる。
 *   - 振替請求あり（投入成功）: 結果反映日（resultOn）の13時から
 *   - 振替請求なし・投入失敗: 実約定日（transferOn）の翌日から
 *   - 振替カレンダーなし: 約定日（scheduledOn）の翌日から
 * いずれも約定日の翌日より前にはならない。約定日がない分割はサーバーでも延滞にならないので null。
 */
const delinquencyStartsAt = (entry: DunningPaymentScheduleEntry): dayjs.Dayjs | null => {
  if (!entry.scheduledOn) return null
  const afterScheduledOn = nextDayStart(entry.scheduledOn)

  if (entry.transferRequested && entry.transferOn && entry.resultOn) {
    return laterOf(afterScheduledOn, dayjs(entry.resultOn).startOf("day").hour(13))
  }
  if (entry.transferOn) return laterOf(afterScheduledOn, nextDayStart(entry.transferOn))
  return afterScheduledOn
}

/**
 * 入金額が約定額に達していれば、payment_status の更新前でも支払済として扱う。
 * サーバーの遅延判定（遅延金額の集計）と同じ条件にそろえ、遅延金額 0円なのに「遅延中」と出るのを防ぐ。
 */
const isFullyPaid = (entry: DunningPaymentScheduleEntry): boolean =>
  entry.paymentStatus === "paid" ||
  (entry.paymentTotalAmount != null &&
    entry.paidAmount != null &&
    entry.paidAmount >= entry.paymentTotalAmount)

export function scheduleRowState(
  entry: DunningPaymentScheduleEntry,
  now: dayjs.Dayjs = dayjs()
): ScheduleRowState {
  if (isFullyPaid(entry)) return "paid"

  const startsAt = delinquencyStartsAt(entry)
  if (startsAt && !now.isBefore(startsAt)) {
    return entry.paymentStatus === "part_paid" ? "part" : "overdue"
  }

  // 遅延になる前は、振替を待っている間だけ「反映待ち」、それ以外は「予定」。
  // 一部入金でも遅延になるまでは「予定」（入金済の列に金額が出る）。
  if (
    entry.transferRequested &&
    entry.transferOn &&
    entry.resultOn &&
    !now.isBefore(dayjs(entry.transferOn).startOf("day"))
  ) {
    return "awaiting"
  }
  return "future"
}

/** 遅延中（未入金・一部入金）の分割か。遅延金額と最古の遅延の強調表示に使う。 */
const isDelinquentScheduleEntry = (
  entry: DunningPaymentScheduleEntry,
  now: dayjs.Dayjs = dayjs()
) => {
  const state = scheduleRowState(entry, now)
  return state === "overdue" || state === "part"
}

const SCHEDULE_STATUS_CONFIG: Record<ScheduleRowState, { label: string; className: string }> = {
  paid: { label: "支払済", className: "bg-green-100 text-green-700" },
  part: { label: "一部入金", className: "bg-yellow-100 text-yellow-700" },
  awaiting: { label: "反映待ち", className: "bg-blue-100 text-blue-700" },
  overdue: { label: "遅延中", className: "bg-red-100 text-red-700" },
  future: { label: "予定", className: "bg-gray-100 text-black" },
}

const paymentPromiseUrl = (paymentPromiseId: number) =>
  `/admin/payment-promises/${paymentPromiseId}`

const movingApplicationUrl = (movingApplicationId: number) =>
  // 各種書類（入居申込の書類確認ページ）
  `/admin/moving-applications/${movingApplicationId}/documents`

const formatInterestRate = (interestRate: number | null) => {
  if (interestRate == null) return "—"

  // 現行値（0.18）と旧データの百分率値（18）の双方を同じ表示にする。
  const percentage = interestRate <= 1 ? interestRate * 100 : interestRate
  return `${new Intl.NumberFormat("ja-JP", { maximumFractionDigits: 2 }).format(percentage)}％`
}

/** 複数契約時にパネル見出しへ付けるスコープ表示。 */
export const ScopeLabel: React.FC<{ number: number; contractId: number; multi: boolean }> = ({
  number,
  contractId,
  multi,
}) => {
  if (!multi) return null
  return (
    <span className="ml-2 text-[1.1rem] font-normal text-black">
      契約{number} ・ {contractId}
    </span>
  )
}

/**
 * ケースに紐付いていない進行中の契約（linked: false で完済でない）。契約情報の見出しの「延滞なし」に使う。
 * linked が無い旧 API の契約は紐付きとして扱うので、falsy ではなく false と比べる
 */
const isUnlinkedOngoingContract = (detail: DunningCaseContractDetail) =>
  detail.linked === false && !detail.paidOff

const hasDelinquentInstallment = (detail: DunningCaseContractDetail, now: dayjs.Dayjs) =>
  (detail.paymentSchedule ?? []).some((entry) => isDelinquentScheduleEntry(entry, now))

/**
 * 表示する契約。完済の契約は左に並ぶので、未選択のときは遅延中の分割がある契約、
 * 紐付いた進行中の契約、その他の進行中の契約の順に選ぶ
 */
export const selectContractDetail = (
  details: DunningCaseContractDetail[],
  selectedContractId: number | null,
  now: dayjs.Dayjs = dayjs()
): DunningCaseContractDetail | undefined =>
  details.find((detail) => detail.contractId === selectedContractId) ??
  details.find((detail) => !detail.paidOff && hasDelinquentInstallment(detail, now)) ??
  details.find((detail) => !detail.paidOff && detail.linked !== false) ??
  details.find((detail) => !detail.paidOff) ??
  details[0]

const UnlinkedContractChip: React.FC = () => (
  <span
    className={`ml-2 inline-block whitespace-nowrap rounded px-2 py-0.5 text-[1.05rem] font-semibold ${NEUTRAL_CHIP_CLASS}`}
  >
    延滞なし
  </span>
)

const PaidOffContractChip: React.FC = () => (
  <span
    className={`ml-2 inline-block whitespace-nowrap rounded px-2 py-0.5 text-[1.05rem] font-semibold ${NEUTRAL_CHIP_CLASS}`}
  >
    完済
  </span>
)

/* ---------- 支払いスケジュール（入金日 = lastPaidAt を合体表示。契約情報パネル内のセクション） ---------- */

const ScheduleSection: React.FC<{
  schedule: DunningPaymentScheduleEntry[]
  principalDue: number | null | undefined
  lateChargeDue: number | null | undefined
  asOf?: string
  readOnly: boolean
}> = ({ schedule, principalDue, lateChargeDue, asOf, readOnly }) => {
  const now = asOf ? dayjs(asOf) : dayjs()
  const oldestOverdueId = useMemo(() => {
    const delinquent = schedule.filter((entry) => isDelinquentScheduleEntry(entry, now))
    if (delinquent.length === 0) return null
    return delinquent.reduce((oldest, current) =>
      (current.scheduledOn ?? "") < (oldest.scheduledOn ?? "") ? current : oldest
    ).paymentPromiseId
  }, [now, schedule])

  const overdueRows = schedule.filter((entry) => isDelinquentScheduleEntry(entry, now))
  const schedulePrincipalDue = overdueRows.reduce(
    (sum, entry) => sum + Math.max((entry.paymentTotalAmount ?? 0) - (entry.paidAmount ?? 0), 0),
    0
  )
  const hasCompiledTotal = principalDue != null || lateChargeDue != null
  const overdueTotal = hasCompiledTotal
    ? (principalDue ?? 0) + (lateChargeDue ?? 0)
    : schedulePrincipalDue

  return (
    <div className="mt-5 border-t border-gray-100 pt-4">
      <h4 className="mb-3 text-[1.2rem] font-semibold text-black">支払いスケジュール</h4>
      {schedule.length === 0 ? (
        <p className="text-[1.3rem] text-black">支払スケジュールがありません。</p>
      ) : (
        <div>
          {/* 未払い金額サマリー */}
          <div className="mb-3 flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-[1.25rem] text-black">
            <span>
              遅延中 <b className="tabular-nums">{formatYen(overdueTotal)}</b>
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {[
                    ["回", "text-left"],
                    ["約定日", "text-left"],
                    ["金額", "text-right"],
                    ["入金済", "text-right"],
                    ["状態", "text-center"],
                    ["入金日", "text-left"],
                    ...(readOnly ? [] : [["PaymentPromise", "text-left"]]),
                  ].map(([label, align]) => (
                    <th
                      key={label}
                      className={`whitespace-nowrap px-3 py-2 text-[1.1rem] font-semibold text-black ${align}`}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {schedule.map((entry) => {
                  const state = scheduleRowState(entry, now)
                  const config = SCHEDULE_STATUS_CONFIG[state]
                  const isOldest = entry.paymentPromiseId === oldestOverdueId
                  return (
                    <tr
                      key={entry.paymentPromiseId}
                      className={isOldest ? "bg-red-50 shadow-[inset_3px_0_0_#ef4444]" : undefined}
                    >
                      <td className="px-3 py-2 text-[1.2rem] tabular-nums text-black">
                        {entry.numberOfPayment != null ? `${entry.numberOfPayment}回` : "—"}
                      </td>
                      <td className="px-3 py-2 text-[1.25rem] font-medium tabular-nums text-black">
                        {formatJapaneseDate(entry.scheduledOn)}
                      </td>
                      <td className="px-3 py-2 text-right text-[1.25rem] font-medium tabular-nums">
                        {formatYen(entry.paymentTotalAmount)}
                      </td>
                      <td className="px-3 py-2 text-right text-[1.25rem] font-medium tabular-nums text-black">
                        {entry.paidAmount ? formatYen(entry.paidAmount) : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-[1.05rem] font-semibold ${config.className}`}
                        >
                          {config.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[1.15rem] tabular-nums text-black">
                        {formatJapaneseDate(entry.lastPaidAt)}
                      </td>
                      {!readOnly && (
                        <td className="px-3 py-2">
                          <a
                            href={paymentPromiseUrl(entry.paymentPromiseId)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[1.2rem] font-medium tabular-nums text-primary underline hover:opacity-70"
                            onClick={(event) => event.stopPropagation()}
                          >
                            #{entry.paymentPromiseId}
                          </a>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

/* ---------- 支払約束（Promise: 督促所有 dunning_payment_promises） ---------- */

/** 置換済・取消済の約束のラベル（有効な約束と前回の延滞の約束は結果で出す） */
const PROMISE_STATUS_CONFIG: Partial<Record<DunningPaymentPromiseStatus, PromiseChipConfig>> = {
  superseded: { label: "置換済", className: NEUTRAL_CHIP_CLASS },
  canceled: { label: "取消済", className: NEUTRAL_CHIP_CLASS },
}

/** 支払約束で選択できる時刻 */
const QUICK_TIMES = ["15:00", "23:59"]

// サーバー（RegisterDunningPaymentPromiseUsecase::PAST_SCHEDULED_AT_MESSAGE）と同じ文言。一致で表示の重複を抑えている
const PAST_PROMISE_MESSAGE = "約束の日時が過ぎています。これからの日時を入れてください"

/**
 * 入力した約束の日時がもう過ぎているか（過ぎた日時は登録できない。サーバーでも弾く）。
 * 23:59 の約束はその日のうちに払えばよいので、その日の終わりまでは過ぎていない扱い（API と同じ規則）。
 */
export const isPastPromiseDateTime = (date: string, time: string, now = dayjs()): boolean => {
  if (date === "" || time === "") return false
  const scheduledAt = dayjs(`${date}T${time}`)
  if (!scheduledAt.isValid()) return false
  const deadline = time === "23:59" ? scheduledAt.endOf("day") : scheduledAt
  return deadline.isBefore(now)
}

/** AIドラフト検証の約束の履歴の結果（支払済み・不履行・期日前） */
const PROMISE_HISTORY_RESULT_CONFIG: Record<
  NonNullable<DunningPaymentPromise["historyResult"]>,
  PromiseChipConfig
> = {
  fulfilled: { label: "支払済", className: "bg-green-100 text-green-700" },
  broken: { label: "不履行", className: "bg-red-100 text-red-700" },
  pending: { label: "期日前", className: NEUTRAL_CHIP_CLASS },
}

/**
 * 有効な約束の結果と、上の欄に出す約束か。判定は API（outcome / current）で、画面はそのまま出す。
 * outcome を返さない API（画面が API より先に出たとき）だけ、これまでの規則（有効な約束は上の欄に出し、
 * broken なら不履行）で補う。前回の延滞の約束（ended）は記録した結果を出し、上の欄には出さない。
 */
const promiseResult = (
  promise: DunningPaymentPromise
): { outcome: DunningPaymentPromiseOutcome | null; current: boolean } => {
  if (promise.status === "ended") return { outcome: promise.outcome ?? null, current: false }
  if (promise.status !== "active") return { outcome: null, current: false }
  if (promise.outcome != null)
    return { outcome: promise.outcome, current: promise.current === true }
  return { outcome: promise.broken ? "broken" : "pending", current: true }
}

/** 知らない状態・結果が来ても落とさず、値をそのまま出す（API が先に出たときのため） */
const promiseChipConfig = (promise: DunningPaymentPromise): PromiseChipConfig => {
  if (promise.historyResult) return PROMISE_HISTORY_RESULT_CONFIG[promise.historyResult]
  const { outcome } = promiseResult(promise)
  return (
    (outcome ? PROMISE_OUTCOME_CONFIG[outcome] : PROMISE_STATUS_CONFIG[promise.status]) ?? {
      label: outcome ?? promise.status,
      className: NEUTRAL_CHIP_CLASS,
    }
  )
}

const PromiseStatusChip: React.FC<{ promise: DunningPaymentPromise }> = ({ promise }) => {
  const config = promiseChipConfig(promise)
  return (
    <span className="inline-flex w-28 items-center gap-1">
      <span
        className={`inline-flex min-w-14 justify-center whitespace-nowrap rounded px-2 py-0.5 text-[1.05rem] font-semibold ${config.className}`}
      >
        {config.label}
      </span>
    </span>
  )
}

/** 左カラム（チャット直下）に置くケース単位の支払約束パネル。 */
export const PromisePanel: React.FC<{
  facts: DunningCaseFacts
  caseId: number
  onUpdated?: (next: DunningCaseItem) => void
  allowRegistration?: boolean
  /** 約束履歴を最初から開いておく（AIドラフト検証の読み取り表示では、不履行の約束を見落とさないようにする） */
  historyInitiallyOpen?: boolean
  registrationUnavailableReason?: string
}> = ({
  facts,
  caseId,
  onUpdated,
  allowRegistration = true,
  historyInitiallyOpen = false,
  registrationUnavailableReason,
}) => {
  const { registerPromise, cancelPromise, isSubmitting, error, clearError } =
    useDunningPaymentPromises(caseId)
  const [date, setDate] = useState("")
  const [time, setTime] = useState("")
  // 入力のあと何も触らずに時刻を過ぎても、押した時点で過ぎていれば送らずに文言を出す
  const [pastOnSubmit, setPastOnSubmit] = useState(false)
  const [amount, setAmount] = useState("")
  const [note, setNote] = useState("")

  // ケース全体の督促所有約束。API の返却順に依存せず、登録日時の新しい順で表示する。
  const promises = [...(facts.dunningPromises ?? [])].sort((left, right) => {
    const createdAtDifference = dayjs(right.createdAt).valueOf() - dayjs(left.createdAt).valueOf()
    return createdAtDifference || right.id - left.id
  })
  // 上の欄に出すのは、約束中の約束か、延滞が続いているケースの不履行の約束だけ（約束中を優先）。
  // 履行・判定不可の約束と、延滞が終わったケースの不履行は約束履歴にだけ出す
  const currentPromises = promises
    .map((promise) => ({ promise, ...promiseResult(promise) }))
    .filter(({ current }) => current)
  const currentPromise =
    currentPromises.find(({ outcome }) => outcome === "pending") ??
    currentPromises.find(({ outcome }) => outcome === "broken")
  const activePromise = currentPromise?.promise
  const activePromiseBroken = currentPromise?.outcome === "broken"

  // 旧データ（BTI 流用時代の facts.promise）は参考表示のみ
  const showLegacyPromise =
    promises.length === 0 || facts.promise.source === "bank_transfer_invoice"
  const legacyScheduledOn = showLegacyPromise
    ? (facts.promise.scheduledOn ?? facts.promise.latestScheduledOn)
    : null

  const pastDateTime = isPastPromiseDateTime(date, time) || pastOnSubmit
  const canSubmit = date !== "" && time !== "" && !pastDateTime && !isSubmitting
  // サーバーが同じ理由（過ぎた日時）で弾いたときは、画面の判定の文言と重ねて出さない
  const showServerError = error != null && !(pastDateTime && error === PAST_PROMISE_MESSAGE)

  // 日時を直したら、前の判定とサーバーのエラーの文言は古くなるので消す
  const changeDate = (next: string) => {
    setDate(next)
    setPastOnSubmit(false)
    clearError()
  }
  const changeTime = (next: string) => {
    setTime(next)
    setPastOnSubmit(false)
    clearError()
  }

  const submit = () => {
    if (!canSubmit) return
    // 描画のあとに時刻を過ぎていることがあるので、送る直前にもう一度判定する
    if (isPastPromiseDateTime(date, time)) {
      setPastOnSubmit(true)
      clearError()
      return
    }
    registerPromise({
      scheduledAtLocal: `${date}T${time}`,
      amount,
      note,
    }).then((next) => {
      if (next && onUpdated) {
        onUpdated(next)
        setDate("")
        setTime("")
        setAmount("")
        setNote("")
      }
    })
  }

  const handleCancel = (promise: DunningPaymentPromise) => {
    if (
      window.confirm(`期日 ${formatDateTime(promise.scheduledAt)} の支払約束を取り消しますか？`)
    ) {
      cancelPromise(promise.id).then((next) => next && onUpdated?.(next))
    }
  }

  return (
    <Panel title="支払約束（Promise）">
      {/* 現在の約束（約束中、または延滞が続いているケースの不履行） */}
      {activePromise ? (
        <div
          className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-3 text-[1.3rem] text-black ${
            activePromiseBroken ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"
          }`}
        >
          {activePromiseBroken ? (
            <span className="font-semibold text-red-700">不履行の約束あり</span>
          ) : (
            <span className="font-semibold text-green-700">有効な約束あり</span>
          )}
          <span className={`tabular-nums ${activePromiseBroken ? "font-medium text-red-700" : ""}`}>
            期日 {formatDateTime(activePromise.scheduledAt)}
          </span>
          {activePromise.amount != null && (
            <span className="tabular-nums">{formatYen(activePromise.amount)}</span>
          )}
          {activePromise.note && (
            <span className="text-[1.2rem] text-black">（{activePromise.note}）</span>
          )}
          {onUpdated && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => handleCancel(activePromise)}
              className="ml-auto rounded-lg border border-gray-300 bg-white px-3 py-1 text-[1.15rem] font-medium text-black hover:text-red-600 disabled:opacity-50"
            >
              取消
            </button>
          )}
        </div>
      ) : (
        <div className="text-[1.3rem] text-black">有効な約束はありません。</div>
      )}

      {legacyScheduledOn && (
        <p className="mt-2 text-[1.1rem] tabular-nums text-black">
          参考: 旧支払約束データの最新約束日 {formatDateTime(legacyScheduledOn)}
        </p>
      )}

      {/* 約束登録フォーム */}
      {onUpdated && allowRegistration && (
        <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1 text-[1.2rem] font-medium text-black">
              約束期日
              <input
                type="date"
                min={dayjs().format("YYYY-MM-DD")}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-[1.3rem] text-black"
                value={date}
                onChange={(event) => changeDate(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-[1.2rem] font-medium text-black">
              時刻
              <span className="flex items-center gap-2">
                <input
                  type="time"
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-[1.3rem] text-black"
                  value={time}
                  onChange={(event) => changeTime(event.target.value)}
                />
                {QUICK_TIMES.map((quick) => (
                  <button
                    key={quick}
                    type="button"
                    aria-pressed={time === quick}
                    onClick={() => changeTime(quick)}
                    className={`rounded-lg border px-2.5 py-1.5 text-[1.15rem] font-medium tabular-nums transition-colors ${
                      time === quick
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-gray-300 bg-white text-black hover:bg-gray-100"
                    }`}
                  >
                    {quick}
                  </button>
                ))}
              </span>
            </label>
            <label className="flex flex-col gap-1 text-[1.2rem] font-medium text-black">
              金額（任意）
              <input
                type="number"
                min={1}
                placeholder="全額なら空欄"
                className="w-40 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[1.3rem] tabular-nums text-black"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </label>
            <label className="min-w-[200px] flex-1 flex-col gap-1 text-[1.2rem] font-medium text-black sm:flex">
              メモ（任意）
              <input
                type="text"
                placeholder="経緯・条件など"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[1.3rem] text-black"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={!canSubmit}
              onClick={submit}
              className="rounded-xl bg-primary px-4 py-2 text-[1.3rem] font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              🤝 約束を登録
            </button>
          </div>
          {pastDateTime && (
            <p className="mt-2 text-[1.2rem] text-red-600">{PAST_PROMISE_MESSAGE}</p>
          )}
          {showServerError && <p className="mt-2 text-[1.2rem] text-red-600">{error}</p>}
        </div>
      )}
      {onUpdated && !allowRegistration && (
        <p className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-[1.2rem] text-black">
          {registrationUnavailableReason ??
            "現在のケースステータスでは、新しい約束は登録できません。"}
        </p>
      )}

      {/* 約束履歴（履行・不履行・判定不可・置換済・取消済・前回の延滞の約束を含む） */}
      {promises.length > 0 && (
        <details className="mt-4" open={historyInitiallyOpen}>
          <summary className="cursor-pointer text-[1.2rem] font-medium text-primary hover:opacity-70">
            約束履歴（{promises.length}件）
          </summary>
          <ul className="mt-2 divide-y divide-gray-100">
            {promises.map((promise) => (
              <li
                key={promise.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[1.25rem]"
              >
                <PromiseStatusChip promise={promise} />
                {promise.status === "ended" && (
                  <span className="text-[1.1rem] text-black">前回の延滞</span>
                )}
                <span className="font-medium tabular-nums text-black">
                  期日 {formatDateTime(promise.scheduledAt)}
                </span>
                <span className="tabular-nums text-black">
                  {promise.amount != null ? formatYen(promise.amount) : "金額指定なし"}
                </span>
                {promise.note && (
                  <span className="text-[1.15rem] text-black">（{promise.note}）</span>
                )}
                <span className="text-[1.1rem] tabular-nums text-black">
                  登録 {formatDateTime(promise.createdAt)}
                </span>
                {promise.status === "active" && onUpdated && (
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => handleCancel(promise)}
                    className="text-[1.2rem] text-black hover:text-red-600 disabled:opacity-50"
                  >
                    取消
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Panel>
  )
}

/* ---------- 契約情報（支払いスケジュールを含む統合パネル） ---------- */

const ContractPanel: React.FC<{
  detail: DunningCaseContractDetail
  contractFacts: DunningCaseFactsContract | undefined
  scope: React.ReactNode
  asOf?: string
  readOnly: boolean
}> = ({ detail, contractFacts, scope, asOf, readOnly }) => {
  // 旧契約では手数料率0%でもinterest_amountがNULLのことがある。
  // 率0%なら業務上の手数料は0円なので、欠損表示にしない。
  const interestAmount = detail.interestAmount ?? (detail.interestRate === 0 ? 0 : null)

  return (
    <Panel
      className={detail.paidOff ? "!border-gray-400 !bg-gray-300 grayscale" : undefined}
      title={
        <>
          契約情報{scope}
          {/* 紐付いていない契約でも、延滞に入ってから取り込み（13時）で紐付くまでは遅延中の分割があるので出さない */}
          {isUnlinkedOngoingContract(detail) &&
            !hasDelinquentInstallment(detail, asOf ? dayjs(asOf) : dayjs()) && (
              <UnlinkedContractChip />
            )}
          {detail.paidOff && <PaidOffContractChip />}
        </>
      }
      headerRight={
        !readOnly && detail.movingApplicationId != null ? (
          <a
            href={movingApplicationUrl(detail.movingApplicationId)}
            target="_blank"
            rel="noreferrer"
            className="text-[1.2rem] font-medium text-primary underline hover:opacity-70"
          >
            各種書類
          </a>
        ) : undefined
      }
    >
      {/* 識別情報と契約条件を左列、金額情報を右列にまとめ、同種の情報を縦方向に比較しやすくする。 */}
      <dl className="grid grid-cols-[120px_1fr] gap-x-5 gap-y-2 text-[1.3rem] sm:grid-cols-[120px_1fr_120px_1fr]">
        {(
          [
            ["契約ID", detail.contractId],
            ["元本", formatYen(detail.loan)],
            ["物件名", detail.propertyName ?? "—"],
            [
              "分割手数料",
              `${formatYen(interestAmount)}（${formatInterestRate(detail.interestRate)}）`,
            ],
            ["分割回数", detail.numberOfPayment != null ? `${detail.numberOfPayment}回` : "—"],
            ["総支払額", formatYen(detail.totalPaymentAmount)],
          ] as [string, React.ReactNode][]
        ).map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-black">{label}</dt>
            <dd className="font-medium tabular-nums text-black">{value}</dd>
          </React.Fragment>
        ))}
      </dl>

      <ScheduleSection
        schedule={detail.paymentSchedule}
        principalDue={contractFacts?.principalDue}
        lateChargeDue={contractFacts?.lateChargeDue}
        asOf={asOf}
        readOnly={readOnly}
      />
    </Panel>
  )
}

const ContractTab: React.FC<{
  detail: DunningCaseContractDetail
  number: number
  isSelected: boolean
  onSelect: () => void
}> = ({ detail, number, isSelected, onSelect }) => (
  <button
    type="button"
    aria-pressed={isSelected}
    onClick={onSelect}
    className={`flex w-[18rem] items-center rounded-lg border px-4 py-1.5 text-left text-[1.25rem] font-medium transition-colors ${
      isSelected
        ? "border-primary bg-primary text-white"
        : "border-gray-300 bg-white text-black hover:border-primary hover:text-[#3aa9c4]"
    }`}
  >
    契約{number}
    <span
      className={`ml-2 text-[1.05rem] font-normal ${isSelected ? "text-white/80" : "text-black"}`}
    >
      {detail.contractId}
    </span>
    <span
      className={`ml-auto text-[1.05rem] font-normal ${
        isSelected ? "text-white/80" : "text-black"
      }`}
    >
      {detail.paidOff
        ? "完済"
        : detail.numberOfPayment != null && `${detail.numberOfPayment}回払い`}
    </span>
  </button>
)

type Props = {
  facts: DunningCaseFacts
  /** 親（DunningCaseDetail）が持つ選択中契約。左カラムの支払約束と連動する */
  selectedContractId: number | null
  onSelectContract: (contractId: number) => void
  /**
   * 読み取り専用の表示（AIドラフト検証の架空ケースなど）。旧オペへのリンク（各種書類・PaymentPromise）を出さず、
   * 支払スケジュールの状態を facts.asOf 時点で判定する
   */
  readOnly?: boolean
}

export const ContractDetailsPanel: React.FC<Props> = ({
  facts,
  selectedContractId,
  onSelectContract,
  readOnly = false,
}) => {
  const allDetails = facts.contractDetails ?? []
  // 白紙契約（active: false）はオペレーターの誤案内防止のためタブに出さない
  const details = allDetails.filter((detail) => detail.active)
  const hiddenCount = allDetails.length - details.length

  const selected = selectContractDetail(
    details,
    selectedContractId,
    readOnly && facts.asOf ? dayjs(facts.asOf) : undefined
  )
  const [paidOffOpen, setPaidOffOpen] = useState(false)
  const paidOffCount = details.filter((detail) => detail.paidOff).length
  const collapsible = paidOffCount > 0
  // 完済の契約を開いている間は畳まない
  const showPaidOffTabs = !collapsible || paidOffOpen || selected?.paidOff === true
  // 番号は表示の順（完済が左）と切り離して固定する（業務の要望）
  const contractNumbers = new Map(
    details
      .map((detail) => detail.contractId)
      .sort((left, right) => left - right)
      .map((contractId, index) => [contractId, index + 1])
  )
  const numberOf = (detail: DunningCaseContractDetail) =>
    contractNumbers.get(detail.contractId) ?? 0
  const multi = details.length > 1

  const scope = selected ? (
    <ScopeLabel number={numberOf(selected)} contractId={selected.contractId} multi={multi} />
  ) : null
  const selectedContractFacts = selected
    ? facts.contracts.find((contract) => contract.contractId === selected.contractId)
    : undefined

  const renderTab = (detail: DunningCaseContractDetail) => (
    <ContractTab
      key={detail.contractId}
      detail={detail}
      number={numberOf(detail)}
      isSelected={detail.contractId === selected?.contractId}
      onSelect={() => onSelectContract(detail.contractId)}
    />
  )

  if (details.length === 0) {
    return (
      <Panel title="契約・支払情報">
        <p className="text-[1.3rem] text-black">アクティブな契約がありません。</p>
      </Panel>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      {/* 契約タブ（複数契約時のみ表示） */}
      {multi && (
        <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          {collapsible ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  aria-expanded={showPaidOffTabs}
                  disabled={selected?.paidOff === true}
                  onClick={() => setPaidOffOpen((open) => !open)}
                  className="flex w-[18rem] items-center justify-between rounded-lg border border-gray-400 bg-gray-200 px-4 py-1.5 text-left text-[1.25rem] font-medium text-black transition-colors hover:border-primary disabled:cursor-default disabled:hover:border-gray-400"
                >
                  <span>
                    完済 <b className="font-semibold tabular-nums">{paidOffCount}件</b>
                  </span>
                  <span aria-hidden="true">{showPaidOffTabs ? "▴" : "▾"}</span>
                </button>
                {details.filter((detail) => !detail.paidOff).map(renderTab)}
              </div>
              {showPaidOffTabs && (
                <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
                  {details.filter((detail) => detail.paidOff).map(renderTab)}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-wrap items-center gap-2">{details.map(renderTab)}</div>
          )}
          <span className="ml-1 text-[1.1rem] text-black">
            契約情報を契約ごとに切替（チャット・約束・ログはケース共通）
            {hiddenCount > 0 && ` ・ 白紙契約 ${hiddenCount}件は非表示`}
          </span>
        </div>
      )}
      {!multi && hiddenCount > 0 && (
        <p className="text-[1.1rem] text-black">白紙契約 {hiddenCount}件は非表示</p>
      )}

      {selected && (
        <ContractPanel
          detail={selected}
          contractFacts={selectedContractFacts}
          scope={scope}
          asOf={readOnly ? facts.asOf : undefined}
          readOnly={readOnly}
        />
      )}
    </div>
  )
}
